import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import {
  AlertCircle,
  ArrowLeft,
  HelpCircle,
  Check,
  Loader2,
} from "lucide-react";

export default function VerifyEmail() {
  const { userId, token } = useParams();
  const navigate = useNavigate();

  const { callApi, loading } = useApi();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accountType, setAccountType] = useState(null);

  const verifyEmail = async () => {
    try {
      const res = await callApi(
        `/auth/merchantVerification/${userId}/${token}`,
        undefined,
        "GET"
      );
      console.log("API RESPONSE:", res);
      setSuccess(res);
      // Determine account type from response
      if (res?.isAgency === true || res?.isAgency === "true") {
        setAccountType("agency");
      } else {
        setAccountType("merchant");
      }
    } catch (err) {
      console.log("API ERROR:", err);
      setError(err.response?.data?.error || "Verification failed");
    }
  };

  useEffect(() => {
    if (!userId || !token) return;
    verifyEmail();
  }, [userId, token]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-4 py-12">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-lg w-full">
        {/* Loading State */}
        {loading && (
          <div className="bg-background/95 backdrop-blur-sm border border-border rounded-2xl shadow-2xl p-10 text-center">
            <div className="mb-6 flex justify-center">
              <Loader2 className="w-12 h-12 text-accent animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Verifying your email...
            </h2>
            <p className="text-muted-foreground">
              Please wait while we verify your email address.
            </p>
          </div>
        )}

        {/* Error State */}
        {error &&
          (() => {
            const errorMessage = String(error).toLowerCase();
            const isAlreadyVerified =
              errorMessage.includes("already verified") ||
              errorMessage.includes("already registered") ||
              errorMessage.includes("already exists");

            return (
              <div className="bg-background/95 backdrop-blur-sm border border-border rounded-2xl shadow-2xl p-8 md:p-10">
                <div className="text-center">
                  {/* Icon */}
                  <div className="mb-8 flex justify-center">
                    <div className="relative">
                      {isAlreadyVerified ? (
                        <>
                          <div className="absolute inset-0 w-24 h-24 bg-accent/20 rounded-full animate-pulse" />
                          <div className="relative w-24 h-24 bg-gradient-to-br from-accent/20 to-accent/10 rounded-full flex items-center justify-center ring-2 ring-accent/30">
                            <Check
                              className="w-12 h-12 text-accent"
                              strokeWidth={2}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="absolute inset-0 w-24 h-24 bg-destructive/20 rounded-full animate-pulse" />
                          <div className="relative w-24 h-24 bg-gradient-to-br from-destructive/20 to-destructive/10 rounded-full flex items-center justify-center ring-2 ring-destructive/30">
                            <AlertCircle
                              className="w-12 h-12 text-destructive"
                              strokeWidth={2}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-foreground mb-4">
                      {isAlreadyVerified
                        ? "Already Verified"
                        : "Verification Failed"}
                    </h2>
                    <div
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${
                        isAlreadyVerified
                          ? "bg-accent/10 border border-accent/30"
                          : "bg-destructive/10 border border-destructive/30"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full animate-pulse ${
                          isAlreadyVerified ? "bg-accent" : "bg-destructive"
                        }`}
                      />
                      <span
                        className={`text-sm font-semibold ${
                          isAlreadyVerified ? "text-accent" : "text-destructive"
                        }`}
                      >
                        {isAlreadyVerified
                          ? "Account Already Active"
                          : "Unable to Verify"}
                      </span>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed mb-2">
                      {String(error)}
                    </p>
                    {!isAlreadyVerified && (
                      <p className="text-sm text-muted-foreground">
                        Please contact our support team if you believe this is
                        an error.
                      </p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-border mb-8" />

                  {/* Action Button */}
                  <button
                    className="w-full px-6 py-4 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-xl transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center gap-2 group"
                    onClick={() => (window.location.href = "/")}
                  >
                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    Go Back to Home
                  </button>
                </div>
              </div>
            );
          })()}

        {/* Success State */}
        {!loading && !error && success && (
          <div className="bg-background/95 backdrop-blur-sm border border-border rounded-2xl shadow-2xl p-4 md:p-10">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mb-8 flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 w-24 h-24 bg-accent/20 rounded-full animate-ping" />
                  <div className="absolute inset-0 w-24 h-24 bg-accent/30 rounded-full animate-pulse" />
                  <div className="relative w-24 h-24 bg-gradient-to-br from-accent to-accent/80 rounded-full flex items-center justify-center shadow-xl">
                    <Check className="w-12 h-12 text-accent-foreground stroke-[3]" />
                  </div>
                </div>
              </div>

              {/* Success Message */}
              <div className="mb-8">
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Email Verified!
                </h2>

                {/* Account Type Badge */}
                {accountType && (
                  <div className=" inline-flex items-center gap-3 px-5 py-3 bg-accent/10 border-2 border-accent/30 rounded-xl">
                    <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-lg">
                      {accountType === "agency" ? (
                        <svg
                          className="w-5 h-5 text-accent-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-accent-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-muted-foreground font-medium">
                        Account Type
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {accountType.charAt(0).toUpperCase() +
                          accountType.slice(1)}{" "}
                        Account
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Information Cards */}
              <div className="mb-8 space-y-2">
                <div className="bg-accent/5 border border-accent/20 rounded-xl p-3 lg:p-5 text-left">
                  <p className="font-bold text-foreground mb-1 flex items-center gap-2">
                    <Check className="w-4 h-4 text-accent" />
                    Your account is ready to use
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You can now access your account and start using AgentDial.
                  </p>
                </div>

                <div className="bg-accent/5 border border-accent/20 rounded-xl p-3 lg:p-5 text-left">
                  <p className="font-bold text-foreground mb-1 flex items-center gap-2">
                    <Check className="w-4 h-4 text-accent" />
                    Login credentials sent to your email
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Username and password have been included in the email.
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="mb-6">
                <button
                  onClick={() => {
                    if (success?.domain) {
                      window.location.href = success.domain;
                    } else {
                      navigate("/");
                    }
                  }}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl hover:scale-[1.02] transform"
                >
                  Go to Home
                </button>
              </div>

              {/* Help Text */}
              <div className="pt-6 border-t border-border">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <HelpCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Check your email for your login credentials.</span>
                  </div>
                  {success?.supportMail && (
                    <a
                      href={`mailto:${success.supportMail}`}
                      className="text-sm text-accent hover:underline font-medium transition-colors"
                    >
                      Contact Support
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
